import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Container, Row, Col, Spinner, Button, Card, Modal } from 'react-bootstrap';
import axios from 'axios';
import { useCarrito } from './CarritoContext';
import { API } from '../../../../api/URL_API';
import { Cart2, Dash, Plus } from 'react-bootstrap-icons';
import { trackViewContent, trackAddToCart } from '../../../../utils/metaPixel';

const currencyFormatter = new Intl.NumberFormat('es-CO', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
});

const formatCurrencyHelper = (amount) => {
    const numberAmount = typeof amount === 'string'
        ? parseFloat(amount.replace(/[^0-9,-]+/g, ''))
        : amount;
    return currencyFormatter.format(numberAmount);
};

const isProductNewHelper = (product) => {
    const rawTime = product?.times || product?.createdAt || product?.created_at || product?.fechaCreacion;
    if (!rawTime) return false;

    const productDate = new Date(rawTime);
    if (Number.isNaN(productDate.getTime())) return false;

    const normalizeToLocalDate = (date) => {
        const localDate = new Date(date);
        localDate.setHours(0, 0, 0, 0);
        return localDate;
    };

    const productStartDate = normalizeToLocalDate(productDate);
    const today = normalizeToLocalDate(new Date());
    const diffInDays = Math.floor((today - productStartDate) / (1000 * 60 * 60 * 24));

    return diffInDays >= 0 && diffInDays <= 3;
};

const ProductCard = React.memo(({
    product,
    quantity,
    isPromo,
    promoConfig,
    isAdded,
    onAddToCart,
    onUpdateQuantity,
    onOpenImage,
    cartTotalQuantity,
    configDescuento,
    reglasDescuento
}) => {
    const finalPrecioU = isPromo
        ? Math.min(promoConfig.precio, product.precioU)
        : product.precioU;

    const potentialTotal = (cartTotalQuantity || 0) + quantity;

    let discountPercent = null;
    let isMayorista = false;
    
    if (configDescuento?.tipoConfig === 'cantidad' && reglasDescuento?.length > 0) {
        const regla = reglasDescuento
            .filter(rule => rule.tipoRegla === 'cantidad')
            .sort((a, b) => b.max_cantidad - a.max_cantidad)
            .find(rule =>
                potentialTotal >= (rule.min_cantidad || 0) &&
                potentialTotal <= (rule.max_cantidad || Infinity)
            );
        
        if (regla) {
            discountPercent = regla.porcentajeDescuento;
        } else {
            isMayorista = true;
        }
    } else if (configDescuento?.tipoConfig === 'desable') {
        discountPercent = 0;
    }

    const priceWithDiscount = discountPercent 
        ? finalPrecioU * (1 - discountPercent / 100) 
        : finalPrecioU;

    return (
        <Card className='w-100 border-1 shadow-sm' style={{ borderColor: '#e9ecef', position: 'relative' }}>
            {isPromo && (
                <div
                    className='position-absolute'
                    style={{
                        top: '10px',
                        left: '10px',
                        zIndex: 10,
                        backgroundColor: '#2E0205',
                        color: '#fff',
                        padding: '4px 10px',
                        borderRadius: '20px',
                        fontSize: '0.75rem',
                        fontWeight: '700',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
                    }}
                >
                    {promoConfig.badge}
                </div>
            )}
            <div
                className='bg-light d-flex align-items-center justify-content-center position-relative'
                style={{
                    width: '100%',
                    aspectRatio: '1 / 1',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                }}
                onClick={() => onOpenImage(product)}
                role='button'
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        onOpenImage(product);
                    }
                }}
                aria-label={`Ver imagen ampliada de ${product.nomProducto}`}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                }}
            >
                {product.isNew && (
                    <div
                        className='position-absolute top-0 start-0 m-2 px-2 py-1 rounded-pill text-white fw-bold'
                        style={{
                            backgroundColor: '#dc3545',
                            fontSize: '0.7rem',
                            zIndex: 10,
                            letterSpacing: '0.4px'
                        }}
                    >
                        NUEVO
                    </div>
                )}
                <Card.Img
                    variant='top'
                    src={product.imgProducto}
                    alt={product.nomProducto}
                    loading='lazy'
                    style={{
                        objectFit: 'contain',
                        padding: '8px',
                        pointerEvents: 'none',
                        width: '100%',
                        height: '100%'
                    }}
                />
            </div>
            <Card.Body className='p-2 p-sm-3 d-flex flex-column'>
                <Row className='g-1 g-sm-2 mb-2 mb-sm-3'>
                    <Col xs={9} sm={9}>
                        <Card.Title
                            className='mb-1 mb-sm-2'
                            style={{
                                fontSize: 'clamp(0.9rem, 2.8vw, 1.15rem)',
                                fontWeight: 700,
                                lineHeight: 1.3,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                color: '#2E0205'
                            }}
                        >
                            {product.nombreOriginal === null
                                ? product.nomProducto
                                : product.nombreOriginal}
                        </Card.Title>
                        <div style={{ fontSize: 'clamp(0.8rem, 2vw, 0.95rem)' }}>
                            {isMayorista ? (
                                <div className='d-flex flex-column mb-1'>
                                    <div className='mb-1 d-flex align-items-center'>
                                        <span className='text-muted fw-bold text-uppercase' style={{ fontSize: '0.75em' }}>Mayorista:</span>
                                        <span className="ms-2 badge" style={{ backgroundColor: '#e2e3e5', color: '#41464c', fontSize: '0.7em', border: '1px solid #d3d6d8' }}>
                                            Aplicado
                                        </span>
                                    </div>
                                    <span className='fw-bold' style={{ color: '#2E0205', fontSize: '1.2em' }}>
                                        ${formatCurrencyHelper(product.precioM)}
                                    </span>
                                </div>
                            ) : (
                                <>
                                    <div className='mb-1'>
                                        <span className='text-muted fw-bold text-uppercase' style={{ fontSize: '0.75em' }}>Unidad:</span>
                                        {isPromo && product.precioU > promoConfig.precio ? (
                                            <div className='d-flex flex-column'>
                                                <span style={{ textDecoration: 'line-through', color: '#6c757d', fontSize: '0.85em' }}>
                                                    ${formatCurrencyHelper(product.precioU)}
                                                </span>
                                                <span style={{ textDecoration: discountPercent ? 'line-through' : 'none', color: discountPercent ? '#6c757d' : '#dc3545', fontSize: discountPercent ? '0.9em' : '1.1em' }}>
                                                    ${formatCurrencyHelper(finalPrecioU)}
                                                </span>
                                            </div>
                                        ) : (
                                            <div className='d-inline-block ms-1'>
                                                <span style={{ textDecoration: discountPercent ? 'line-through' : 'none', color: discountPercent ? '#6c757d' : '#2E0205', fontSize: discountPercent ? '0.9em' : '1.05em', fontWeight: discountPercent ? 'normal' : 'bold' }}>
                                                    ${formatCurrencyHelper(finalPrecioU)}
                                                </span>
                                            </div>
                                        )}
                                        
                                        {discountPercent > 0 && (
                                            <div className='d-flex align-items-center mt-1'>
                                                <span className='fw-bold' style={{ color: '#dc3545', fontSize: '1.15em' }}>
                                                    ${formatCurrencyHelper(priceWithDiscount)}
                                                </span>
                                                <span className="ms-2 badge" style={{ backgroundColor: '#f8d7da', color: '#842029', fontSize: '0.7em', border: '1px solid #f5c2c7', fontWeight: '600' }}>
                                                    -{discountPercent}%
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </Col>
                    <Col xs={3} sm={3} className='d-flex align-items-start justify-content-end'>
                        <Button
                            onClick={() => onAddToCart({ ...product, precioU: finalPrecioU, quantity })}
                            disabled={product.stock <= 0}
                            className='p-0 rounded-circle border-0'
                            style={{
                                width: 'clamp(40px, 9vw, 48px)',
                                height: 'clamp(40px, 9vw, 48px)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: isAdded ? '#198754' : '#2E0205',
                                transform: isAdded ? 'scale(1.15)' : 'scale(1)',
                                transition: 'all 0.25s ease',
                                opacity: product.stock <= 0 ? 0.5 : 1,
                                flexShrink: 0
                            }}
                        >
                            {isAdded ? (
                                <span style={{ color: 'white', fontSize: '1rem' }}>✓</span>
                            ) : (
                                <>
                                    <Cart2 size={16} className='d-sm-none' />
                                    <Cart2 size={20} className='d-none d-sm-block' />
                                </>
                            )}
                        </Button>
                    </Col>
                </Row>
                <div className='bg-light p-1 p-sm-2 rounded d-flex gap-1 gap-sm-2'>
                    <Button
                        size='sm'
                        variant='outline-danger'
                        onClick={() => onUpdateQuantity(product.idProducto, quantity - 1)}
                        disabled={quantity <= 1}
                        className='p-0'
                        style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 24px', fontSize: '0.7rem' }}
                    >
                        <Dash size={14} />
                    </Button>
                    <input
                        type='number'
                        min="1"
                        max={product.stock}
                        value={quantity || 1}
                        onChange={(e) => {
                            let newQuantity = parseInt(e.target.value, 10) || 1;
                            newQuantity = Math.max(1, Math.min(newQuantity, product.stock));
                            onUpdateQuantity(product.idProducto, newQuantity);
                        }}
                        disabled={product.stock <= 0}
                        className='form-control form-control-sm text-center'
                        style={{ flex: 1, fontWeight: 600, fontSize: 'clamp(0.7rem, 2vw, 0.85rem)' }}
                    />
                    <Button
                        size='sm'
                        variant='outline-success'
                        onClick={() => onUpdateQuantity(product.idProducto, quantity + 1)}
                        disabled={quantity >= product.stock}
                        className='p-0'
                        style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 24px', fontSize: '0.7rem' }}
                    >
                        <Plus size={14} />
                    </Button>
                </div>
            </Card.Body>
        </Card>
    );
});

function ProductosUni({ idCategoria }) {
    const [products, setProducts] = useState([]);
    const [quantities, setQuantities] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [productsPerPage] = useState(12);
    const [totalProducts, setTotalProducts] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [hasLoadedInitialData, setHasLoadedInitialData] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [showImageModal, setShowImageModal] = useState(false);
    const [addedProductId, setAddedProductId] = useState(null);

    const loaderRef = useRef(null);
    const observer = useRef();
    const { addToCarrito, selectedCategories, carrito, configDescuento, reglasDescuento } = useCarrito();

    const cartTotalQuantity = useMemo(() => {
        return carrito ? carrito.reduce((total, product) => total + (product.quantity || 1), 0) : 0;
    }, [carrito]);

    useEffect(() => {
        window.scrollTo(0, 0);
        const loaderElement = loaderRef.current;
        return () => {
            // Limpiar observador al desmontar
            if (loaderElement && observer.current) {
                observer.current.unobserve(loaderElement);
            }
        };
    }, []);

    const openImageModal = useCallback((product) => {
        setSelectedImage(product.imgProducto);
        setShowImageModal(true);
        trackViewContent(product);
    }, []);

    const closeImageModal = useCallback(() => {
        setShowImageModal(false);
        setSelectedImage(null);
    }, []);

    const fetchProducts = useCallback(async (page = 1, isInitialLoad = false) => {
        try {
            if (isInitialLoad) {
                setIsLoading(true);
                setHasLoadedInitialData(false);
                setProducts([]);
                setQuantities({});
                setHasMore(true); // Reiniciar paginación al inicio
            } else {
                setIsLoadingMore(true);
            }

            let url;
            let params = {
                limit: productsPerPage,
                offset: (page - 1) * productsPerPage,
                _: Date.now()
            };

            if (selectedCategories.length > 0) {
                url = `${API}/api/mostrarCategorias/productos-por-categorias`;
                params.categoryIds = selectedCategories.join(',');
            } else if (idCategoria) {
                url = `${API}/api/mostrarCategorias/productos/${idCategoria}`;
                params.page = page;
            }

            const response = await axios.get(url, { params, headers: { 'Cache-Control': 'no-cache' } });

            console.log('Respuesta API página', page, response.data);

            if (response.data) {
                let productsData = [];
                let receivedTotal = 0;

                if (Array.isArray(response.data)) {
                    productsData = response.data;
                    receivedTotal = response.data.length;
                } else if (response.data.products && Array.isArray(response.data.products)) {
                    productsData = response.data.products;
                    receivedTotal = response.data.total || response.data.products.length;
                }

                const processedProducts = productsData.map(product => ({
                    ...product,
                    precioU: typeof product.precioU === 'string' ? parseFloat(product.precioU.replace(/[^0-9,-]+/g, '')) : product.precioU,
                    precioM: typeof product.precioM === 'string' ? parseFloat(product.precioM.replace(/[^0-9,-]+/g, '')) : product.precioM,
                    isNew: isProductNewHelper(product)
                }));

                setProducts(prev => {
                    const existingIds = new Set(prev.map(p => p.idProducto));
                    const newProducts = processedProducts.filter(p => !existingIds.has(p.idProducto));
                    const updatedProducts = [...prev, ...newProducts];

                    setHasMore(updatedProducts.length < receivedTotal);

                    return updatedProducts;
                });

                setTotalProducts(receivedTotal);

                setQuantities(prev => {
                    const newQuantities = { ...prev };
                    processedProducts.forEach(product => {
                        if (!newQuantities[product.idProducto]) {
                            newQuantities[product.idProducto] = 1;
                        }
                    });
                    return newQuantities;
                });

                // Si no recibimos productos nuevos, ya no hay más para cargar
                if (processedProducts.length === 0) {
                    setHasMore(false);
                }
            }
        } catch (error) {
            console.error('Error al cargar productos:', error);
            setHasMore(false);
        } finally {
            if (isInitialLoad) {
                setIsLoading(false);
                setHasLoadedInitialData(true);
            } else {
                setIsLoadingMore(false);
            }
        }
    }, [selectedCategories, idCategoria, productsPerPage]);


    useEffect(() => {
        // Resetear estado cuando cambian las categorías o idCategoria
        setCurrentPage(1);
        fetchProducts(1, true);
    }, [fetchProducts]);

    const handleAddToCart = useCallback((product) => {
        const quantityToCart = product.quantity || 1;
        
        addToCarrito({
            idProducto: product.idProducto,
            referencia: product.referencia,
            nomProducto: product.nombreOriginal || product.nomProducto,
            precioU: product.precioU,
            precioM: product.precioM,
            image: product.imgProducto || 'URL_DEFAULT_IMAGE',
            quantity: quantityToCart,
            stock: product.stock,
        });

        trackAddToCart(product, quantityToCart);

        setAddedProductId(product.idProducto);
        setTimeout(() => {
            setAddedProductId(null);
        }, 1200);
    }, [addToCarrito]);

    const updateQuantity = useCallback((productId, newQuantity) => {
        setQuantities(prev => {
            const p = products.find(prod => prod.idProducto === productId);
            return {
                ...prev,
                [productId]: Math.max(1, Math.min(newQuantity, p?.stock || 1))
            };
        });
    }, [products]);

    // Configuración del IntersectionObserver
    useEffect(() => {
        if (!hasMore || isLoading || isLoadingMore) return;

        const options = {
            root: null,
            rootMargin: '100px',
            threshold: 0
        };

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setCurrentPage(prev => {
                    const nextPage = prev + 1;
                    fetchProducts(nextPage).then(() => {
                        // Después del fetch, si no hay más, no incrementamos el page
                        if (!hasMore) return prev;
                    });
                    return nextPage;
                });
            }
        }, options);

        if (loaderRef.current) {
            observer.current.observe(loaderRef.current);
        }

        return () => {
            if (observer.current) {
                observer.current.disconnect();
            }
        };
    }, [hasMore, isLoading, isLoadingMore, fetchProducts]);


    const visibleProducts = useMemo(() => {
        return products.slice(0, currentPage * productsPerPage);
    }, [products, currentPage, productsPerPage]);

    useEffect(() => {
        console.log(`Mostrando ${visibleProducts.length} de ${totalProducts} productos`);
    }, [visibleProducts, totalProducts]);

    // CONFIGURACION DE DESCUENTO GLOBAL PARA PROMOCIONES (Preparado para API)
    const promocionesConfig = useMemo(() => ({
        'bolso-y-portacelular': {
            precio: 75000,
            badge: 'DESCUENTO'
        },
    }), []);

    return (
        <>
            <Container fluid className='py-3 py-sm-4 color-bg-secundary'>
                {/* Encabezado minimalista */}
                <div className='text-center mb-3 mb-sm-5 px-2'>
                    <h2 className='fw-bold text-uppercase mb-0' style={{ fontSize: 'clamp(1.25rem, 4vw, 1.75rem)', letterSpacing: '0.5px', color: '#2E0205' }}>
                        {idCategoria.replace(/-/g, " ")}
                    </h2>
                </div>

                <Row className='g-2 g-sm-3 px-1 px-sm-0'>
                    {isLoading && !isLoadingMore ? (
                        <Col xs={12} className='text-center py-5'>
                            <Spinner animation='border' role='status' variant='dark' className='mb-3' style={{ width: '2.5rem', height: '2.5rem' }} />
                            <p className='text-dark fw-bold' style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}>Cargando...</p>
                        </Col>
                    ) : !isLoading && !isLoadingMore && visibleProducts.length === 0 && hasLoadedInitialData ? (
                        <Col xs={12} className='text-center py-5'>
                            <p className='text-muted' style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}>No hay productos disponibles.</p>
                        </Col>
                    ) : (
                        visibleProducts.map((product) => {
                            const promoConfig = promocionesConfig[idCategoria];
                            const isPromo = !!promoConfig;

                            return (
                                <Col key={product.idProducto} xs={6} sm={6} md={4} lg={3} className='d-flex'>
                                    <ProductCard
                                        product={product}
                                        quantity={quantities[product.idProducto] || 1}
                                        isPromo={isPromo}
                                        promoConfig={promoConfig}
                                        isAdded={addedProductId === product.idProducto}
                                        onAddToCart={handleAddToCart}
                                        onUpdateQuantity={updateQuantity}
                                        onOpenImage={openImageModal}
                                        cartTotalQuantity={cartTotalQuantity}
                                        configDescuento={configDescuento}
                                        reglasDescuento={reglasDescuento}
                                    />
                                </Col>
                            );
                        })
                    )}
                </Row>

                {/* Loader */}
                {isLoadingMore && (
                    <Row className='mt-3 mt-sm-4'>
                        <Col xs={12} className='text-center py-3'>
                            <Spinner animation="border" size="sm" variant='danger' />
                        </Col>
                    </Row>
                )}

                <div ref={loaderRef} style={{ height: '20px' }} />
            </Container>

            {/* Modal para ver imagen ampliada */}
            <Modal
                show={showImageModal}
                onHide={closeImageModal}
                centered
                contentClassName='bg-transparent border-0 shadow-none'
                backdropClassName='bg-dark'
            >
                <Modal.Body
                    className='p-0 d-flex justify-content-center align-items-center'
                    style={{
                        background: 'transparent'
                    }}
                >
                    {selectedImage && (
                        <div
                            className='position-relative d-inline-block'
                            style={{
                                maxWidth: '95vw',
                                maxHeight: '95vh'
                            }}
                        >
                            <img
                                src={selectedImage}
                                alt='Imagen ampliada'
                                style={{
                                    width: 'auto',
                                    height: 'auto',
                                    maxWidth: '95vw',
                                    maxHeight: '95vh',
                                    objectFit: 'contain',
                                    borderRadius: '12px',
                                    display: 'block'
                                }}
                            />

                            <Button
                                variant='dark'
                                onClick={closeImageModal}
                                className='position-absolute d-flex align-items-center justify-content-center p-0 border-0'
                                style={{
                                    top: '10px',
                                    right: '10px',
                                    width: '42px',
                                    height: '42px',
                                    borderRadius: '50%',
                                    zIndex: 10,
                                    fontSize: '1.2rem',
                                    lineHeight: 1,
                                    background: 'rgba(0,0,0,0.7)',
                                    backdropFilter: 'blur(4px)'
                                }}
                            >
                                ✕
                            </Button>
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
}

export default ProductosUni;